import type { ButtonNode, MenuNode, MenuSchema, TextInputNode, TextNode } from '../MenuSchema.js';

export const ACCOUNT_MENU_ACTIONS = {
    saveProfile: 'account-save-profile',
    changePassword: 'account-change-password',
} as const;

export const ACCOUNT_MENU_LABELS = {
    title: 'Account',
    username: 'Username',
    usernameLoading: 'Username: loading…',
    usernamePrefix: 'Username:',
    tokenBalance: 'Token Balance',
    tokenBalancePrefix: 'Token Balance:',
    tokenBalanceLoading: 'Token Balance: loading…',
    displayName: 'Display Name',
    displayNamePlaceholder: 'Display Name',
    email: 'Email',
    emailPlaceholder: 'Email address',
    saveChanges: 'Save Changes',
    saveDisplayName: 'Save Display Name',
    currentPassword: 'Current Password',
    currentPasswordPlaceholder: 'Current password',
    newPassword: 'New Password',
    newPasswordPlaceholder: 'New password (min 8 chars)',
    confirmPassword: 'Confirm New Password',
    confirmPasswordPlaceholder: 'Confirm new password',
    changePassword: 'Change Password',
    notAuthenticated: 'Not authenticated.',
    displayNameSaved: 'Display name saved.',
    saveFailed: 'Failed to save.',
    networkError: 'Network error.',
} as const;

export function createAccountMenuSchema(): MenuSchema {
    return {
        id: 'ACCOUNT',
        title: ACCOUNT_MENU_LABELS.title,
        nodes: [
            { type: 'text', text: ACCOUNT_MENU_LABELS.username },
            { type: 'text', text: ACCOUNT_MENU_LABELS.tokenBalance },
            { type: 'input', id: 'account-display-name-input', placeholder: ACCOUNT_MENU_LABELS.displayNamePlaceholder, get: () => '' },
            { type: 'input', id: 'account-email-input', placeholder: ACCOUNT_MENU_LABELS.emailPlaceholder, get: () => '' },
            { type: 'button', id: ACCOUNT_MENU_ACTIONS.saveProfile, label: ACCOUNT_MENU_LABELS.saveChanges },
            { type: 'input', id: 'account-current-password-input', placeholder: ACCOUNT_MENU_LABELS.currentPasswordPlaceholder, get: () => '' },
            { type: 'input', id: 'account-new-password-input', placeholder: ACCOUNT_MENU_LABELS.newPasswordPlaceholder, get: () => '' },
            { type: 'input', id: 'account-confirm-password-input', placeholder: ACCOUNT_MENU_LABELS.confirmPasswordPlaceholder, get: () => '' },
            { type: 'button', id: ACCOUNT_MENU_ACTIONS.changePassword, label: ACCOUNT_MENU_LABELS.changePassword },
        ],
    };
}

function findSchemaNodeById(schema: MenuSchema, id: string): MenuNode | undefined {
    return schema.nodes.find((n) => (n as { id?: string }).id === id);
}

export function readAccountSchemaButtonLabel(schema: MenuSchema, id: string, fallback: string): string {
    const node = findSchemaNodeById(schema, id) as ButtonNode | undefined;
    if (!node || node.type !== 'button') {
        return fallback;
    }
    return typeof node.label === 'function' ? node.label() : node.label;
}

export function readAccountSchemaInputPlaceholder(schema: MenuSchema, id: string, fallback: string): string {
    const node = findSchemaNodeById(schema, id) as TextInputNode | undefined;
    if (!node || node.type !== 'input') {
        return fallback;
    }
    return node.placeholder || fallback;
}

export function readAccountSchemaTextAt(schema: MenuSchema, index: number, fallback: string): string {
    const textNodes = schema.nodes.filter((n): n is TextNode => n.type === 'text');
    const node = textNodes[index];
    if (!node) {
        return fallback;
    }
    return typeof node.text === 'function' ? node.text() : node.text;
}
